import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronDown, ChevronRight, MapPin, ShoppingCart, Heart, Search, Camera, Mic,
  LayoutGrid, Eye, Play, Star, Radio, Flame, Map, List, BadgeCheck, Phone, Clock, ArrowRight, Store,
} from "lucide-react";
import "./vertical.css";
import { useHomeTheme, HomeThemeToggle } from "@/components/HomeThemeToggle";

const Flag = () => (
  <span className="vt-flag" aria-hidden="true">
    <span style={{ background: "#F77F00" }} /><span style={{ background: "#FFFFFF" }} /><span style={{ background: "#009E60" }} />
  </span>
);

const SectionHead = ({ icon, title, subtitle, right, testid }) => (
  <div className="vt-sechead" data-testid={testid}>
    {icon}
    <div>
      <h2>{title}</h2>
      <p>{subtitle}</p>
    </div>
    <div className="vt-sechead-right">{right}</div>
  </div>
);

export default function VerticalPage({ data }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [query, setQuery] = useState("");
  const [ville, setVille] = useState(params.get("ville") || "Abidjan");
  const [villeOpen, setVilleOpen] = useState(false);
  const [villeQ, setVilleQ] = useState("");
  const [villes, setVilles] = useState([]);
  useEffect(() => {
    if (!villeOpen || villes.length) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v3/search/places-list`)
      .then((r) => r.json()).then((j) => j.ok && setVilles(j.places)).catch(() => {});
  }, [villeOpen, villes.length]);
  const [cartCount, setCartCount] = useState(3);
  const [nearbyView, setNearbyView] = useState("carte");
  const [favs, setFavs] = useState({});
  const [homeTheme, setHomeTheme] = useHomeTheme();
  const isCommerce = data.id === "commerce";
  const [ayaShops, setAyaShops] = useState([]);
  useEffect(() => {
    if (!isCommerce) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boutique`)
      .then((r) => r.json()).then((j) => { if (j.ok) setAyaShops(j.shops); }).catch(() => {});
  }, [isCommerce]);

  useEffect(() => {
    document.title = `AYA — ${data.hero.title}`;
    window.scrollTo(0, 0);
  }, [data]);

  const soon = (label) => toast.info(`${label} — bientôt disponible.`);
  const goAya = (ctx) => navigate(ctx ? `/aya-prive?ctx=${encodeURIComponent(ctx)}` : "/aya-prive");
  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    const ctx = data.id === "sante"
      ? `J'ai une question santé : ${q}. Aide-moi (pharmacies, médicaments, conseils) à ${ville}.`
      : data.id === "services"
        ? `J'ai besoin d'un professionnel : ${q}. Trouve-moi les meilleurs près de moi à ${ville}.`
        : data.id === "location"
          ? `Je cherche à louer : ${q}. Trouve-moi les meilleures offres de location près de moi à ${ville}.`
          : data.id === "bien-etre"
          ? `Je cherche une prestation bien-être : ${q}. Trouve-moi les meilleures options près de moi à ${ville}.`
          : isCommerce
            ? `Je cherche à acheter : ${q}. Aide-moi à trouver les meilleures options à ${ville}.`
            : `J'ai besoin d'aide pour la démarche administrative suivante : ${q}.`;
    goAya(ctx);
  };
  const addToCart = (name) => { setCartCount((c) => c + 1); toast.success(`${name} ajouté au panier`); };
  const toggleFav = (id) => setFavs((f) => ({ ...f, [id]: !f[id] }));

  const HeroIcon = data.hero.Icon;

  return (
    <div className={`vt-page vt-page-${data.id}`} data-aya-home={homeTheme} data-testid={`vertical-page-${data.id}`}>
      <div className="vt-topzone">
        <img src={data.hero.photo || "/aya/verticals/vert_hero_woman.jpg"} alt="" className="vt-hero-photo" loading="eager" />

        {/* HEADER */}
        <header className="vt-header">
          <button className="vt-back" data-testid="vt-back-btn" aria-label="Retour" onClick={() => navigate("/search")}>
            <ChevronLeft size={18} />
          </button>
          <div className="vt-brand">
            <img src="/aya/mask-small.png" alt="AYA" className="vt-brand-mask" />
            <div>
              <div className="vt-brand-name">AYA</div>
              <div className="vt-brand-sub">Tout un pays, dans votre main <Flag /></div>
            </div>
          </div>
          <div className="vt-head-right">
            <HomeThemeToggle theme={homeTheme} onChange={setHomeTheme} />
            <span style={{ position: "relative" }}>
              <button className="vt-loc" data-testid="vt-location-btn" onClick={() => setVilleOpen(!villeOpen)}>
                <MapPin size={12} color="#0E7A4F" /> {ville} <ChevronDown size={12} />
              </button>
              {villeOpen && (
                <div className="vt-villemenu" data-testid="vt-ville-menu">
                  <input autoFocus placeholder="Rechercher une ville, commune, quartier…" value={villeQ} onChange={(e) => setVilleQ(e.target.value)} data-testid="vt-ville-search" />
                  <div className="ls">
                    {["Abidjan", ...villes.filter((v) => v.name !== "Abidjan").map((v) => v.name)]
                      .filter((n) => !villeQ || n.toLowerCase().includes(villeQ.toLowerCase()))
                      .slice(0, 30)
                      .map((n) => (
                        <button key={n} data-testid={`vt-ville-${n}`} className={n === ville ? "on" : ""} onClick={() => { setVille(n); setVilleOpen(false); setVilleQ(""); }}>
                          <MapPin size={11} color="#0E7A4F" /> {n}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </span>
            <button className="vt-iconbtn" data-testid="vt-cart-btn" aria-label="Panier" onClick={() => soon("Le panier")}>
              <ShoppingCart size={15} />
              {cartCount > 0 && <span className="vt-cart-badge" data-testid="vt-cart-badge">{cartCount}</span>}
            </button>
            <button className="vt-iconbtn" data-testid="vt-fav-btn" aria-label="Favoris" onClick={() => soon("Les favoris")}>
              <Heart size={15} />
            </button>
          </div>
        </header>

        {/* HERO */}
        <div className="vt-hero" data-testid="vt-hero">
          <div className="vt-hero-row">
            <span className="vt-hero-icon" style={{ background: data.hero.iconBg }}>
              <HeroIcon size={19} color="#fff" strokeWidth={2.3} />
            </span>
            <h1 className="vt-hero-title">{data.hero.title}</h1>
          </div>
          <p className="vt-hero-sub">{data.hero.subtitle}</p>
          <div className="vt-hero-tagline">
            <span className="t1">{data.hero.taglineTop}</span>
            <span className="t2">{data.hero.taglineGreen} <Flag /></span>
            <span className="t3">{data.hero.taglineSub}</span>
          </div>
        </div>
      </div>

      <div className="vt-container">
        {/* SEARCH */}
        <form className="vt-search" onSubmit={submitSearch} data-testid="vt-search-form">
          <Search size={17} color="var(--vt-ic2, #9DB4A6)" style={{ flexShrink: 0 }} />
          <input
            data-testid="vt-search-input"
            placeholder={data.searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={data.searchPlaceholder}
          />
          <button type="button" className="plainbtn" data-testid="vt-search-camera" aria-label="Recherche visuelle" onClick={() => soon("La recherche visuelle")}>
            <Camera size={17} color="var(--vt-ic2, #CBD5C0)" />
          </button>
          <button type="submit" className="micbtn" data-testid="vt-search-submit" aria-label="Rechercher">
            <Mic size={16} color="#fff" strokeWidth={2.4} />
          </button>
        </form>

        {/* BOUTIQUES AYA PRO publiées (univers Commerce uniquement) */}
        {isCommerce && ayaShops.length > 0 && (
          <>
            <SectionHead
              testid="vt-ayashops-head"
              icon={<span className="vt-sechead-icon" style={{ background: "rgba(16,185,129,0.18)", border: "1px solid rgba(16,185,129,0.4)" }}><Store size={15} color="#2EE27B" /></span>}
              title="Boutiques AYA"
              subtitle="Les commerçants AYA PRO près de chez vous"
            />
            <div className="vt-carousel" data-testid="vt-ayashops-carousel">
              {ayaShops.map((b) => (
                <button key={b.slug} className="vt-catcard" data-testid={`vt-ayashop-${b.slug}`} onClick={() => navigate(`/boutique/${b.slug}`)}>
                  <div className="imgwrap">
                    <img src={b.banner_image || b.logo_image} alt={b.name} loading="lazy" />
                    {b.is_live && <span className="cicon" style={{ background: "#DC2626", width: "auto", padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 900, color: "#fff" }}>🔴 LIVE</span>}
                  </div>
                  <div className="body">
                    <h3>{b.name}</h3>
                    <p>{b.rating ? `★ ${String(b.rating).replace(".", ",")} (${b.reviews_count}) · ` : ""}{b.city || b.tagline}</p>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* CATEGORIES */}
        <SectionHead
          testid="vt-categories-head"
          icon={<span className="vt-sechead-icon" style={{ background: "rgba(232,163,61,0.18)", border: "1px solid rgba(232,163,61,0.4)" }}><LayoutGrid size={15} color="#E8A33D" /></span>}
          title={data.categories.title}
          subtitle={data.categories.subtitle}
          right={<button className="vt-link" data-testid="vt-categories-more" onClick={() => soon("Toutes les catégories")}>Voir tout <ChevronRight size={12} /></button>}
        />
        <div className="vt-carousel" data-testid="vt-categories-carousel">
          {data.categories.items.map((c) => {
            const CIcon = c.Icon;
            return (
              <button key={c.id} className="vt-catcard" data-testid={`vt-cat-${c.id}`} onClick={() => soon(`Catégorie ${c.title}`)}>
                <div className="imgwrap">
                  <img src={c.img} alt={c.title} loading="lazy" />
                  <span className="cicon" style={{ background: c.iconBg }}><CIcon size={14} color="#fff" strokeWidth={2.3} /></span>
                </div>
                <div className="body">
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* CTA BANNER (Services : « Besoin maintenant ? » / Bien-être : « à domicile ») */}
        {data.ctaBanner && (() => {
          const BIcon = data.ctaBanner.Icon;
          return (
            <div className="vt-ctabanner" data-testid="vt-cta-banner">
              <div className="left">
                <span className="bicon" style={{ background: data.ctaBanner.iconBg }}><BIcon size={17} color="#fff" strokeWidth={2.3} /></span>
                <div className="txt">
                  <h3>{data.ctaBanner.titlePlain} <em>{data.ctaBanner.titleAccent}</em></h3>
                  <p>{data.ctaBanner.text}</p>
                </div>
              </div>
              <div className="right">
                <img src={data.ctaBanner.img} alt="" loading="lazy" />
                <button className="cta" data-testid="vt-cta-banner-btn" onClick={() => goAya(data.ctaBanner.ctx)}>
                  {data.ctaBanner.btn} <ChevronRight size={13} />
                </button>
              </div>
            </div>
          );
        })()}

        {/* NEARBY */}
        <SectionHead
          testid="vt-nearby-head"
          icon={<span className="vt-sechead-icon" style={{ background: "rgba(16,185,129,0.18)", border: "1px solid rgba(16,185,129,0.4)" }}><MapPin size={15} color="#34D399" /></span>}
          title={data.nearby.title}
          subtitle={data.nearby.subtitle}
          right={
            <>
              <button className={`vt-pillbtn ${nearbyView === "carte" ? "active" : ""}`} data-testid="vt-nearby-map-btn" onClick={() => { setNearbyView("carte"); navigate("/map-explorer"); }}>
                <Map size={12} /> Carte
              </button>
              <button className={`vt-pillbtn ${nearbyView === "liste" ? "active" : ""}`} data-testid="vt-nearby-list-btn" onClick={() => { setNearbyView("liste"); soon("La vue liste"); }}>
                <List size={12} /> Liste
              </button>
              <button className="vt-link" data-testid="vt-nearby-more" onClick={() => soon("Plus de résultats à proximité")}>Voir plus <ChevronRight size={12} /></button>
            </>
          }
        />
        {data.nearby.variant === "pros" ? (
          <div className="vt-progrid" data-testid="vt-pros-grid">
            {data.nearby.items.map((n) => (
              <article key={n.id} className="vt-procard" data-testid={`vt-pro-${n.id}`} onClick={() => navigate(`/pro/${n.id}`)} style={{ cursor: "pointer" }}>
                <div className="top">
                  <img src={n.img} alt={n.name} loading="lazy" />
                  <div className="info">
                    <h3>{n.name} {n.verified && <BadgeCheck size={13} color="#2EE27B" style={{ flexShrink: 0 }} />}</h3>
                    <span className="rate"><Star size={11} color="#F5C445" fill="#F5C445" /> {n.rating} <em>({n.reviews} avis)</em></span>
                    <span className="loc"><MapPin size={10} /> {n.area} · {n.distance}</span>
                  </div>
                </div>
                <div className="tags">{n.tags.map((t) => <i key={t}>{t}</i>)}</div>
                <div className="foot">
                  {n.availability === "now"
                    ? <span className="dispo now"><span className="dot" /> Disponible maintenant</span>
                    : <span className="dispo resa"><Clock size={11} /> Sur réservation</span>}
                  <button className="call" aria-label={`Contacter ${n.name}`} data-testid={`vt-pro-call-${n.id}`} onClick={(e) => { e.stopPropagation(); soon(`Contact ${n.name}`); }}>
                    <Phone size={13} color="#fff" fill="#fff" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : data.nearby.variant === "rentals" ? (
          <div className="vt-rentgrid" data-testid="vt-rentals-grid">
            {data.nearby.items.map((n) => (
              <article key={n.id} className="vt-rentcard" data-testid={`vt-rent-${n.id}`} onClick={() => navigate(`/bien/${n.id}`)} style={{ cursor: "pointer" }}>
                <button className="fav" aria-label={`Favori ${n.name}`} data-testid={`vt-rent-fav-${n.id}`} onClick={(e) => { e.stopPropagation(); toggleFav(n.id); }}>
                  <Heart size={14} color={favs[n.id] ? "#F97316" : "var(--vt-ic2, #CFE3D4)"} fill={favs[n.id] ? "#F97316" : "none"} />
                </button>
                <div className="top">
                  <img src={n.img} alt={n.name} loading="lazy" />
                  <div className="info">
                    <h3>{n.name} {n.verified && <BadgeCheck size={13} color="#2EE27B" style={{ flexShrink: 0 }} />}</h3>
                    <span className="rate"><Star size={11} color="#F5C445" fill="#F5C445" /> {n.rating} <em>({n.reviews} avis)</em></span>
                    <span className="loc"><MapPin size={10} /> {n.area} · {n.distance}</span>
                  </div>
                </div>
                <div className="tags">{n.tags.map((t) => <i key={t}>{t}</i>)}</div>
                <div className="pricefoot">
                  <span className="price">
                    {n.price_prefix && <em>{n.price_prefix} </em>}
                    <strong>{Number(n.price_fcfa).toLocaleString("fr-FR")} FCFA</strong>
                    {n.price_unit && <i> {n.price_unit}</i>}
                  </span>
                  <button className="rentcta" data-testid={`vt-rent-cta-${n.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/bien/${n.id}?book=1`); }}>
                    {n.cta} <ArrowRight size={12} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
        <div className="vt-carousel" data-testid="vt-nearby-carousel">
          {data.nearby.items.map((n) => (
            <button key={n.id} className="vt-nearcard" data-testid={`vt-nearby-${n.id}`} onClick={() => soon(n.name)}>
              <span className="vt-nearlogo" style={{ background: n.logo.bg }}>
                {n.logo.type === "icon"
                  ? <n.logo.Icon size={20} color={n.logo.color} strokeWidth={2.4} />
                  : <span className="logotext" style={{ color: n.logo.color }}>{n.logo.text}</span>}
              </span>
              <span style={{ minWidth: 0 }}>
                <h3>{n.name}</h3>
                <span className="row"><MapPin size={10} /> {n.area}</span>
                <span className="meta">
                  <span className="row" style={{ marginTop: 0 }}><MapPin size={10} /> {n.distance}</span>
                  <span className="vt-open">Ouvert</span>
                </span>
              </span>
            </button>
          ))}
        </div>
        )}

        {/* AYA LIVE */}
        <SectionHead
          testid="vt-live-head"
          icon={<span className="vt-sechead-icon" style={{ background: "rgba(220,38,38,0.15)", border: "1px solid rgba(220,38,38,0.4)" }}><Radio size={15} color="#EF4444" /></span>}
          title={data.live.title}
          subtitle={data.live.subtitle}
          right={<button className="vt-link" data-testid="vt-live-more" onClick={() => soon("Tous les lives")}>Voir tous les lives <ChevronRight size={12} /></button>}
        />
        <div className="vt-carousel" data-testid="vt-live-carousel">
          {data.live.items.map((l) => (
            <article key={l.id} className="vt-livecard" data-testid={`vt-live-${l.id}`}>
              <div className="imgwrap">
                <img src={l.img} alt={l.title} loading="lazy" />
                <span className="vt-badge-live">EN DIRECT</span>
                <span className="vt-badge-viewers"><Eye size={11} /> {l.viewers}</span>
                <span className="vt-playcircle"><Play size={12} color="#E8A33D" fill="#E8A33D" /></span>
              </div>
              <div className="body">
                <h3>{l.title}</h3>
                <span className="vt-livetag">{l.tag}</span>
                <button className="vt-livebtn" data-testid={`vt-live-watch-${l.id}`} onClick={() => toast.info(`Le live « ${l.title} » démarre bientôt.`)}>
                  <Play size={11} fill="currentColor" /> Voir le live
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* TRENDING / DEMARCHES — section optionnelle (absente sur Services / Bien-être) */}
        {data.trending && (<>
        <SectionHead
          testid="vt-trending-head"
          icon={<span className="vt-sechead-icon" style={{ background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.4)" }}><Flame size={15} color="#F97316" /></span>}
          title={data.trending.title}
          subtitle={data.trending.subtitle}
          right={<button className="vt-link" data-testid="vt-trending-more" onClick={() => soon("Plus de résultats")}>Voir plus <ChevronRight size={12} /></button>}
        />
        <div className="vt-carousel" data-testid="vt-trending-carousel">
          {data.trending.items.map((p) => (
            <article key={p.id} className="vt-prodcard" data-testid={`vt-trend-${p.id}`}>
              <div className="imgwrap">
                <img src={p.img} alt={p.name} loading="lazy" />
                <button className="vt-fav" data-testid={`vt-trend-fav-${p.id}`} aria-label="Ajouter aux favoris" onClick={() => toggleFav(p.id)}>
                  <Heart size={13} fill={favs[p.id] ? "#EF4444" : "none"} color={favs[p.id] ? "#EF4444" : "#fff"} />
                </button>
              </div>
              <div className="body">
                <h3>{p.name}</h3>
                <span className="desc">
                  {p.status ? <><MapPin size={9} color="var(--vt-icon, #34D399)" /> <span style={{ color: "var(--vt-icon, #34D399)", fontWeight: 700 }}>{p.status}</span></> : p.desc}
                </span>
                <span className="vt-rating">
                  <Star size={11} color="#F5C445" fill="#F5C445" />
                  <span className="val">{p.rating}</span>
                  <span style={{ color: "#8AA394" }}>({p.reviews})</span>
                </span>
                {p.price && <span className="vt-price">{p.price}</span>}
                <span className="spacer" />
                {data.trending.ctaType === "cart" ? (
                  <button className="vt-prodbtn" data-testid={`vt-trend-cta-${p.id}`} onClick={() => addToCart(p.name)}>
                    <ShoppingCart size={12} /> Ajouter au panier
                  </button>
                ) : (
                  <button className="vt-prodbtn" data-testid={`vt-trend-cta-${p.id}`} onClick={() => goAya(`Je veux démarrer la démarche : ${p.name}. Guide-moi étape par étape.`)}>
                    <ShoppingCart size={12} /> Démarrer
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
        </>)}

        {/* TRUST + ASK AYA */}
        <div className="vt-bottom">
          <div className="vt-trust" data-testid="vt-trust-bar">
            {data.trust.map((t, i) => {
              const TIcon = t.Icon;
              return (
                <div className="item" key={i}>
                  <TIcon size={22} color={t.iconColor || "var(--vt-icon, #ECF5EE)"} strokeWidth={1.8} style={{ flexShrink: 0 }} />
                  <div>
                    <h4>{t.title}</h4>
                    <p>{t.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="vt-ask" data-testid="vt-ask-aya-btn" onClick={() => goAya()}>
            <img src="/aya/mask-small.png" alt="" />
            <span className="t">
              <h4>Demander à AYA</h4>
              <p>Je vous accompagne</p>
            </span>
            <span className="mic"><Mic size={16} color="#fff" /></span>
          </button>
        </div>
      </div>
    </div>
  );
}
